import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from "@angular/core";
import {UntypedFormBuilder, Validators} from "@angular/forms";
import {DealState} from "src/app/clearpath-module/store/state";
import {User} from "src/app/user-admin-module/models";
import {UserService} from "src/app/user-admin-module/services";
import {takeUntil} from "rxjs/operators";
import {Subject} from "rxjs";
import {AuthService} from "../../../../auth-module/services";
import {USER_ROLES} from "../../../../app.config";

@Component({
  selector: "app-staff-form",
  templateUrl: "./staff-form.component.html",
  styleUrls: ["./staff-form.component.scss"]
})
export class StaffFormComponent implements OnInit, OnDestroy {
  private unsubscribe$ = new Subject();

  @Input() set deal(deal: DealState) {
    this.syncDealData(deal);
  }

  @Output() submitSalesPerson = new EventEmitter<User>();
  @Output() submitSalesManager = new EventEmitter<User>();
  @Output() flagUnsavedChanges = new EventEmitter<boolean>();

  currentUser: User;

  private uiState = {
    deal: null,
    formOptions: {
      salesPeople: null,
      salesManagers: null,
    }
  };

  salesTeamForm = this.formBuilder.group({
    salesPersonEmployeeId: ["", Validators.required],
    salesManagerEmployeeId: ["", Validators.required]
  });

  constructor(
    private formBuilder: UntypedFormBuilder,
    private userService: UserService,
    private authService: AuthService,
  ) {
  }

  ngOnInit() {
    this.authService.selectUser()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((user: User) => {
        this.currentUser = user;
        this.getSalesPeople();
        this.getSalesManagers();
      });
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  // INITIALIZATION

  private getSalesPeople() {
    this.userService.getSalesPeople()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (salesPeople: User[]) => {
          salesPeople = salesPeople.sort((a, b) =>
            (a.firstName + ' ' + a.lastName).localeCompare(b.firstName + ' ' + b.lastName));
          this.uiState.formOptions.salesPeople = salesPeople || [];

          // console.log("Current User", this.currentUser);
          const deal: DealState = this.uiState.deal;
          if (deal) {
            const salesPersonObject = (salesPeople).find(user => {
              return user.employeeId === deal.salesId;
            });
             console.log("Deal: Current Sales Person", deal.salesId, salesPersonObject);
            if (deal.salesId && this.isSalesPerson(salesPersonObject)) {
              // console.log("Deal: Current Sales Manager", deal.dealId, deal.managerId, deal.salesManager);
              this.salesTeamForm.patchValue({
                salesPersonEmployeeId: deal.salesId
              });
            } else if (this.isSalesPerson(this.currentUser)) {
              // console.log("Current User is Sales Manager", this.currentUser.employeeId, this.currentUser.firstName, this.currentUser.lastName);
              // console.log("Setting Sales Person to Current User", this.currentUser.employeeId, this.currentUser.firstName, this.currentUser.lastName);
              this.salesTeamForm.patchValue({
                salesPersonEmployeeId: this.currentUser.employeeId
              });
              this.submitSalesPerson.emit(this.currentUser);
              // this.flagChange()
            } else {
              console.log("No Sales Person Selected");
              this.salesTeamForm.get("salesPersonEmployeeId").markAsTouched();
            }
          }
        }
      });
  }

  private getSalesManagers() {
    this.userService.getSalesManagers()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (salesManagers: User[]) => {
          salesManagers = salesManagers.sort((a, b) =>
            (a.firstName + ' ' + a.lastName).localeCompare(b.firstName + ' ' + b.lastName));
          this.uiState.formOptions.salesManagers = salesManagers || [];

          // console.log("Current User", this.currentUser);
          const deal = this.uiState.deal;
          if (deal) {
            // console.log("Deal: Current Sales Manager", deal.dealId, deal.managerId, deal.salesManager);
            if (deal.managerId) {
              // console.log("Deal: Current Sales Manager", deal.dealId, deal.managerId, deal.salesManager);
              this.salesTeamForm.patchValue({
                salesManagerEmployeeId: deal.managerId
              });
            } else if (this.isSalesManager(this.currentUser)) {
              // console.log("Current User is Sales Manager", this.currentUser.employeeId, this.currentUser.firstName, this.currentUser.lastName);
              // console.log("Setting Sales Manager to Current User", this.currentUser.employeeId, this.currentUser.firstName, this.currentUser.lastName);
              this.salesTeamForm.patchValue({
                salesManagerEmployeeId: this.currentUser.employeeId
              });
              this.submitSalesManager.emit(this.currentUser);
              // this.flagChange()
            } else {
              console.log("No Sales Manager Selected");
              this.salesTeamForm.get("salesManagerEmployeeId").markAsTouched();
            }
          }
        }
      });
  }

  isSalesManager(user: User): boolean {
    if (!user) {
      return;
    }
    return User.unpackSecurityGroups(user.securityGroups).includes(USER_ROLES.sales_manager);
  }

  isSalesPerson(user: User): boolean {
    if (!user) {
      return;
    }
    return User.unpackSecurityGroups(user.securityGroups).includes(USER_ROLES.sales_floor);
  }

  private syncDealData(deal: DealState) {
    this.uiState.deal = deal;

    /*this.salesTeamForm.patchValue({
      salesPersonEmployeeId: deal.salesId,
      salesManagerEmployeeId: deal.managerId
    });

    const completedDeal = deal.status === DealStatus.Completed;
    if (completedDeal) {
      this.salesTeamForm.disable();
    }*/
  }

  // UI CONTROL & RENDERING

  get salesPeople(): User[] {
    return this.uiState.formOptions.salesPeople || [];
  }

  get salesManagers(): User[] {
    return this.uiState.formOptions.salesManagers || [];
  }

  get salesPerson(): string {
    const {salesId, salesPerson} = this.uiState.deal;
    return salesId ? salesPerson : "---";
  }

  get salesManager(): string {
    const {managerId, salesManager} = this.uiState.deal;
    console.log("Sale Managers:", this.salesManagers);
    const managerObject = (this.salesManagers || []).find(mgr => {
      return mgr.employeeId === managerId;
    });
    return managerId && managerObject ?
      `${managerObject.firstName} ${managerObject.lastName}` :
      "---";
  }

  // FORM HELPER

  autoSubmitSalesPerson() {
    const {salesPersonEmployeeId: employeeId} = this.salesTeamForm.value;
    if (!employeeId) {
      return alert("Sales Person has no employee ID.");
    }
    const salesPerson = this.salesPeople.find(employee => employeeId === employee.employeeId);
    this.submitSalesPerson.emit(salesPerson);
    this.flagChange()
  }

  autoSubmitSalesManager() {
    const {salesManagerEmployeeId: employeeId} = this.salesTeamForm.value;
    if (!employeeId) {
      return alert("Sales Manager has no employee ID.");
    }
    const salesManager = this.salesManagers.find(employee => employeeId === employee.employeeId);
    this.salesTeamForm.patchValue({
      salesManagerEmployeeId: salesManager.employeeId
    });
    this.submitSalesManager.emit(salesManager);
    this.salesTeamForm.get("salesManagerEmployeeId").markAsTouched();
    this.salesTeamForm.get("salesManagerEmployeeId").markAsDirty()
    console.log("invalid?",this.salesTeamForm.invalid);
    this.flagChange()
  }

  touchedInvalid(controlName: string): boolean {
    const control = this.salesTeamForm.get(controlName);
    return control.touched && control.invalid;
  }

  flagChange() {
    console.log("Flagging Unsaved Changes");
    this.flagUnsavedChanges.emit(true);
  }

}
